import React from "react";

import {
  CheckCircle2,
  Clock3,
  FileClock,
  LockKeyhole,
  XCircle,
} from "lucide-react";

/**
 * BdcStatusCards — tarjetas terminales del paso nativo de Buró de Crédito.
 *
 * Presentacional. El orquestador elige la variante según el estado del
 * requirement (`Valid`/`PendingReview`) o de `bdc/status`.
 */

export type BdcStatusVariant =
  | "completed"
  | "in_review"
  | "invalid"
  | "long_report"
  | "read_only";

export interface BdcStatusCardsProps {
  variant: BdcStatusVariant;
  /** Sobrescribe el título por defecto. */
  title?: string;
  /** Sobrescribe la descripción por defecto. */
  description?: string;
  /** Contenido extra opcional (p.ej. un botón de reintento). */
  children?: React.ReactNode;
}

const COMPLETED_TITLE = "Autorización completada";
const COMPLETED_DESCRIPTION =
  "Tu autorización ya fue registrada. No necesitas volver a iniciar este proceso.";

const IN_REVIEW_TITLE = "Autorización enviada correctamente";
const IN_REVIEW_DESCRIPTION =
  "Tu autorización ya fue enviada. Estamos validando la información y no es necesario repetir el proceso.";

const INVALID_TITLE = "Reporte invalidado";
const INVALID_DESCRIPTION =
  "El reporte de Buró de Crédito asociado a este expediente fue invalidado. Por favor contacta al equipo de soporte para continuar.";
const LONG_REPORT_TITLE = "Consulta en proceso";
const LONG_REPORT_DESCRIPTION =
  "La consulta sigue en proceso. Puedes actualizar su estado o continuar con otros documentos.";
const READ_ONLY_TITLE = "Autorización de Buró de Crédito";
const READ_ONLY_DESCRIPTION =
  "Este paso se completa desde el expediente del participante. El personal operativo puede consultar su estado, pero no iniciar ni confirmar el NIP.";

export const BdcStatusCards: React.FC<BdcStatusCardsProps> = ({
  variant,
  title,
  description,
  children,
}) => {
  if (variant === "long_report") {
    return (
      <div
        className="py-16 px-4 sm:px-6 flex flex-col items-center text-center"
        role="status"
        aria-live="polite"
      >
        <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-6 ring-8 ring-blue-50/50">
          <FileClock className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          {title ?? LONG_REPORT_TITLE}
        </h3>
        <p className="text-sm text-slate-500 max-w-md leading-relaxed">
          {description ?? LONG_REPORT_DESCRIPTION}
        </p>
        {children && <div className="mt-4">{children}</div>}
      </div>
    );
  }

  if (variant === "read_only") {
    return (
      <div
        className="py-16 px-4 sm:px-6 flex flex-col items-center text-center"
        role="status"
      >
        <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-6 ring-8 ring-slate-100/60">
          <LockKeyhole className="w-8 h-8 text-slate-500" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          {title ?? READ_ONLY_TITLE}
        </h3>
        <p className="text-sm text-slate-500 max-w-md leading-relaxed">
          {description ?? READ_ONLY_DESCRIPTION}
        </p>
        {children && <div className="mt-4">{children}</div>}
      </div>
    );
  }

  if (variant === "invalid") {
    return (
      <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-6 ring-8 ring-red-50/50">
          <XCircle className="w-8 h-8 text-red-500" strokeWidth={1.5} />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">
          {title ?? INVALID_TITLE}
        </h3>
        <p className="text-sm text-slate-500 max-w-md leading-relaxed">
          {description ?? INVALID_DESCRIPTION}
        </p>
        {children && <div className="mt-4">{children}</div>}
      </div>
    );
  }

  const isCompleted = variant === "completed";

  return (
    <div className="py-16 px-4 sm:px-6 flex flex-col items-center text-center">
      <div
        className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 ring-8 ${
          isCompleted
            ? "bg-green-50 ring-green-50/50"
            : "bg-blue-50 ring-blue-50/50"
        }`}
      >
        {isCompleted ? (
          <CheckCircle2 className="w-8 h-8 text-green-600" strokeWidth={1.5} />
        ) : (
          <Clock3 className="w-8 h-8 text-blue-600" strokeWidth={1.5} />
        )}
      </div>
      <h3 className="text-xl font-semibold text-slate-900 mb-2">
        {title ?? (isCompleted ? COMPLETED_TITLE : IN_REVIEW_TITLE)}
      </h3>
      <p className="text-sm text-slate-500 max-w-md leading-relaxed">
        {description ??
          (isCompleted ? COMPLETED_DESCRIPTION : IN_REVIEW_DESCRIPTION)}
      </p>
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
};

export default BdcStatusCards;
